import React, { useState, useEffect, useCallback } from "react";
import { get, isNumber } from "lodash";
import { format } from "mathjs";

import { Input, Icon, glyphs } from "@treelab/panda";

import { ITypeOptions, ColumnType, NumberFormat } from "../../../../../../types/table-type";
import { IFilterValue } from "../../../../../../types/form-type";

import {
  getFormattedNumber,
  parseStringToFloatForNumber,
  isInvalidTypedCharForNumberInput,
} from "../../../../../../utils/filter_secondary";
import { usePrevious } from "../../../../../../utils/hooks";

interface IProps {
  typeOptions: ITypeOptions<ColumnType.NUMBER>;
  onChange: (value: { [key: string]: any }) => void;
  value: IFilterValue;
}

const NumberInput: React.FC<IProps> = ({ typeOptions, onChange, value: propsValue }) => {
  const isPercentageNumber = get(typeOptions, "numberFormat") === NumberFormat.PERCENTAGE;
  const suppressFormatting = true;
  const value = propsValue.filterString;

  const [localValue, setLocalValue] = useState<any>(
    isNumber(value) ? getFormattedInputValue(value, typeOptions, suppressFormatting) : null
  );
  const [lastValidValue, setLastValidValue] = useState<number | null>(null);
  const [isActive, setIsActive] = useState(false);

  /** We will *ONLY* read updates from parent if we are not focusing on the target */
  const prevValue = usePrevious(value);
  useEffect(() => {
    if (!isActive && value !== prevValue) {
      setLocalValue(value === null ? "" : getFormattedInputValue(value, typeOptions, suppressFormatting));
    }
  }, [isActive, value, typeOptions, suppressFormatting, prevValue]);

  /**
   * When it is changing, we need to make sure that we are
   * 1. Constantly updating local value. It can diverge from true parent value. ie. "..1.24.12."
   * 2. Empty is valid. It means the user removed everything.
   * 3. If it is valid, than we can either send `null` or the fixed precision value.
   * 4. Store the last valid value.
   */
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(e.target.value);

      if (e.target.value === "") {
        setLastValidValue(null);
        if (onChange) onChange({ filterString: null });
      } else {
        // This is type number
        const parsedValue = parseStringToFloatForNumber(e.target.value, typeOptions);
        if (parsedValue === null) return;

        setLastValidValue(parsedValue);
        // Make sure we remove all the numbers from precision that we dont need
        if (onChange) onChange({ filterString: parsedValue });
      }
    },
    [typeOptions, onChange]
  );

  /**
   * On blur, we will do the following:
   * 1. If local value is empty or null, it makes we already did `onChange` on the user clearing value
   * 2. We need to convert the localValue into a valid number by parseFloating it.
   * 3. Compare converted localValue with lastValidValue (remember, lastValidValue should be exactly what parent has)
   * 4. If there is a difference, then we send a new value to the parent.
   */
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (localValue === "" || localValue === null) return;
      // This is type number
      const numberValue = parseStringToFloatForNumber(localValue, typeOptions);

      if (numberValue !== lastValidValue) {
        if (numberValue === null) {
          setLocalValue(null);
          if (onChange) onChange({ filterString: null });
        } else {
          // This is type string
          const finalNumber = getFormattedInputValue(numberValue, typeOptions, suppressFormatting);
          const oldNumber = getFormattedInputValue(value, typeOptions, suppressFormatting);

          setLocalValue(finalNumber);
          // Need to send a number;
          if (onChange && finalNumber !== oldNumber) onChange({ filterString: numberValue });
        }
      } else {
        setLocalValue(numberValue === null ? "" : getFormattedInputValue(numberValue, typeOptions, suppressFormatting));
      }

      setIsActive(false);
    },
    [lastValidValue, localValue, typeOptions, suppressFormatting, value, onChange]
  );

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsActive(true);
  }, []);

  const handleKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isInvalidTypedCharForNumberInput(e, typeOptions, suppressFormatting)) {
        e.preventDefault();
      }
    },
    [typeOptions, suppressFormatting]
  );

  return (
    <Input.Text
      // {...otherProps}
      input={{
        name: "21",
        onChange: handleChange,
        // @ts-ignore
        onBlur: handleBlur,
        // @ts-ignore
        onFocus: handleFocus,
        value: localValue === null ? "" : localValue,
      }}
      onKeyDown={handleKeydown}
      meta={{}}
      trailing={isPercentageNumber && <Icon icon={glyphs.PERCENTAGE} size={{ width: 14, height: 14 }} />}
    />
  );
};

const getFormattedInputValue = (
  value: number,
  typeOptions: ITypeOptions<ColumnType.NUMBER>,
  suppressFormatting?: boolean
) => {
  if (suppressFormatting) return getInputValueWithoutFormatting(value, typeOptions);
  return getFormattedNumber(value, typeOptions);
};

const getInputValueWithoutFormatting = (value: number, typeOptions: ITypeOptions<ColumnType.NUMBER>) => {
  const { numberFormat } = typeOptions;

  if (numberFormat === NumberFormat.PERCENTAGE) {
    return isNumber(value) ? format(value * 100, { precision: 14 }) : "";
  }
  return isNumber(value) ? value.toString() : "";
};

export default NumberInput;
